import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Dropzone } from '../../Dropzone.component';
import FileComponent from '../../Input/File.component';
import ButtonComponent from '../../Button.component';
import {
  ATTACHMENT_ACCEPTED_EXTENSIONS,
  ATTACHMENT_ACCEPTED_MIME_FORMATS,
  ATTACHMENT_MAX_FILE_SIZE,
} from '../../../constants/attachment.constant';
import { fileValidation } from '../../../services/validator.service';

const StyledUploadBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledUploadHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.LABEL};
`;

export interface UploadCvInlineModalProps {
  disabled?: boolean;
  isUploading?: boolean;
  onPendingChange?: (pending: boolean) => void;
  onUpload: (file: File) => Promise<void> | void;
}

const UploadCvInlineModal = ({
  disabled = false,
  isUploading = false,
  onUpload,
  onPendingChange,
}: UploadCvInlineModalProps) => {
  const { t } = useTranslation();

  const [file, setFile] = useState<File | undefined>(undefined);
  const [inputKey, setInputKey] = useState(0);
  const [validationError, setValidationError] = useState<string>('');

  useEffect(() => {
    onPendingChange?.(!!file);
  }, [file, onPendingChange]);

  const isBlocked = disabled || isUploading;

  const validateFile = useCallback((nextFile?: File) => {
    if (!nextFile) {
      setValidationError('');
      return false;
    }

    const validate = fileValidation({
      acceptedFormats: ATTACHMENT_ACCEPTED_MIME_FORMATS,
      maxSize: ATTACHMENT_MAX_FILE_SIZE,
    });

    const msg = validate(nextFile as Partial<File>);
    setValidationError(msg);
    return !msg;
  }, []);

  const handleDrop = useCallback(
    (files: File[]) => {
      if (!files.length || isBlocked) return;
      const next = files[0];
      const ok = validateFile(next);
      setFile(ok ? next : undefined);
    },
    [isBlocked, validateFile],
  );

  const handleChoose = useCallback(
    (next: unknown) => {
      if (isBlocked) return;
      const nextFile = next instanceof File ? next : undefined;
      const ok = validateFile(nextFile);
      setFile(ok ? nextFile : undefined);
    },
    [isBlocked, validateFile],
  );

  const handleUpload = useCallback(async () => {
    if (!file || isBlocked) return;
    if (!validateFile(file)) return;

    try {
      await onUpload(file);
      setFile(undefined);
      setInputKey(prev => prev + 1);
      setValidationError('');
    } catch {
      // Keep selected file so the user can retry.
    }
  }, [file, isBlocked, onUpload, validateFile]);

  return (
    <StyledUploadBlock>
      <StyledUploadHint>{t('calendarEvent.uploadCvHint')}</StyledUploadHint>

      {isBlocked ? (
        <StyledUploadHint>{file?.name}</StyledUploadHint>
      ) : (
        <Dropzone onDrop={handleDrop}>
          <div>
            <FileComponent
              key={inputKey}
              accept={ATTACHMENT_ACCEPTED_EXTENSIONS}
              disabled={isBlocked}
              value={file}
              onChange={handleChoose}
              validationFailureMessage={validationError || undefined}
            />
          </div>
        </Dropzone>
      )}

      {isUploading && (
        <StyledUploadHint>{t('calendarEvent.uploading')}</StyledUploadHint>
      )}

      <ButtonComponent
        appearance="primary"
        disabled={isBlocked || !file || !!validationError}
        handleClick={handleUpload}
      >
        {t('calendarEvent.uploadCv')}
      </ButtonComponent>
    </StyledUploadBlock>
  );
};

export default UploadCvInlineModal;
